import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";

interface BreadcrumbProps {
  pageTitle: string;
  /** Actions for the page, shown beside the title. */
  actions?: React.ReactNode;
}

/**
 * A page's title and the trail back to Home.
 *
 * The trail is worth keeping — it is real wayfinding. The bug was the heading:
 * this rendered the page title as an `h2`, so ten route pages had no
 * first-level heading at all and their outline started at level two. It is an
 * `h1` now, which fixes every one of those call sites without touching them.
 */
const PageBreadcrumb: React.FC<BreadcrumbProps> = ({ pageTitle, actions }) => {
  const { t } = useTranslation();

  return (
    <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0 space-y-2">
        <nav aria-label={t("common.breadcrumb", "Breadcrumb")}>
          <ol className="flex items-center gap-1.5">
            <li>
              <Link
                className="inline-flex items-center gap-1 rounded-md text-sm text-content-secondary hover:text-content-primary"
                to="/"
              >
                {t("sidebar.home")}
              </Link>
            </li>
            <li aria-hidden="true" className="text-content-tertiary">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="truncate text-sm text-content-secondary" aria-current="page">
              {pageTitle}
            </li>
          </ol>
        </nav>
        <h1 className="text-2xl font-semibold tracking-tight text-content-primary sm:text-3xl">
          {pageTitle}
        </h1>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>
      )}
    </header>
  );
};

export default PageBreadcrumb;
